import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check, Loader2, MapPin, Pencil, Plus, Star, Trash2, X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import type { CustomerAddress } from '@/types'

/**
 * Address book section of the customer profile dialog (F-FE-N30).
 *
 * Reads and writes the same customer_addresses rows the customer manages in
 * their online-ordering account and the counter prefills from — one source of
 * truth. List + add + inline edit + make-default + delete.
 */
export function AdminCustomerAddresses({ customerID }: { customerID: string }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const [adding, setAdding] = useState(false)
  const [editingID, setEditingID] = useState<string | null>(null)
  const [label, setLabel] = useState('')
  const [address, setAddress] = useState('')

  const listQuery = useQuery({
    queryKey: ['customer', customerID, 'addresses'],
    queryFn: () => apiClient.listCustomerAddresses(customerID),
    staleTime: 15_000,
  })
  const addresses = listQuery.data?.data ?? []

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['customer', customerID, 'addresses'] })
    // The counter autocomplete caches addresses per search term.
    queryClient.invalidateQueries({ queryKey: ['counter-customer-search'] })
  }

  const fail = (title: string) => (err: unknown) => {
    const msg = err instanceof Error ? err.message : String(err)
    toast({ title, description: msg, variant: 'destructive' })
  }

  // Mutations surface backend `success:false` too (axios doesn't throw on 4xx here).
  const guard = (res: { success: boolean; message: string }, title: string) => {
    if (!res.success) {
      toast({ title, description: res.message || 'Save failed', variant: 'destructive' })
      return false
    }
    invalidate()
    return true
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = { label: label.trim(), address: address.trim() }
      if (editingID) {
        // Preserve an existing pin — this form only edits the text fields,
        // and dropping lat/lng here would silently unpin the address.
        const prev = addresses.find((a) => a.id === editingID)
        return apiClient.updateCustomerAddress(customerID, editingID, {
          ...body,
          latitude: prev?.latitude,
          longitude: prev?.longitude,
          place_id: prev?.place_id,
        })
      }
      return apiClient.addCustomerAddress(customerID, body)
    },
    onSuccess: (res) => {
      if (!guard(res, 'Address not saved')) return
      closeForm()
    },
    onError: fail('Address not saved'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.deleteCustomerAddress(customerID, id),
    onSuccess: (res) => void guard(res, 'Address not removed'),
    onError: fail('Address not removed'),
  })

  const defaultMutation = useMutation({
    mutationFn: (id: string) => apiClient.setCustomerDefaultAddress(customerID, id),
    onSuccess: (res) => void guard(res, 'Default not updated'),
    onError: fail('Default not updated'),
  })

  function openAdd() {
    setEditingID(null)
    setLabel('')
    setAddress('')
    setAdding(true)
  }

  function openEdit(a: CustomerAddress) {
    setAdding(false)
    setEditingID(a.id)
    setLabel(a.label ?? '')
    setAddress(a.address)
  }

  function closeForm() {
    setAdding(false)
    setEditingID(null)
    setLabel('')
    setAddress('')
  }

  const formOpen = adding || editingID !== null
  const canSave = address.trim() !== '' && !saveMutation.isPending

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          Addresses
        </h3>
        {!formOpen && (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={openAdd}
            className="h-7 gap-1.5 text-xs"
          >
            <Plus className="h-3 w-3" />
            Add address
          </Button>
        )}
      </div>

      {listQuery.isLoading ? (
        <div className="flex items-center gap-2 rounded-lg border border-border/80 p-3 text-xs text-muted-foreground">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading addresses…
        </div>
      ) : addresses.length === 0 && !formOpen ? (
        <p className="rounded-lg border border-dashed border-border/80 p-3 text-xs text-muted-foreground">
          No saved addresses. Delivery orders save the customer's address here
          automatically; you can also add one manually.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {addresses.map((a) =>
            editingID === a.id ? (
              <AddressForm
                key={a.id}
                label={label}
                address={address}
                setLabel={setLabel}
                setAddress={setAddress}
                saving={saveMutation.isPending}
                canSave={canSave}
                onSave={() => saveMutation.mutate()}
                onCancel={closeForm}
              />
            ) : (
              <li
                key={a.id}
                className="flex items-start gap-2 rounded-lg border border-border/80 p-2.5"
              >
                <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1 space-y-0.5">
                  <div className="flex flex-wrap items-center gap-1.5">
                    {a.label && (
                      <span className="text-xs font-medium text-foreground">{a.label}</span>
                    )}
                    {a.is_default && (
                      <Badge
                        variant="outline"
                        className="px-1.5 py-0 text-[10px] uppercase tracking-wider"
                      >
                        Default
                      </Badge>
                    )}
                  </div>
                  <p className="break-words text-xs text-muted-foreground">{a.address}</p>
                </div>
                <div className="flex shrink-0 items-center gap-0.5">
                  {!a.is_default && (
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      title="Make default"
                      disabled={defaultMutation.isPending}
                      onClick={() => defaultMutation.mutate(a.id)}
                      className="h-7 w-7 p-0 text-muted-foreground"
                    >
                      <Star className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    title="Edit"
                    onClick={() => openEdit(a)}
                    className="h-7 w-7 p-0 text-muted-foreground"
                  >
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    title="Delete"
                    disabled={deleteMutation.isPending}
                    onClick={() => deleteMutation.mutate(a.id)}
                    className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </li>
            ),
          )}
        </ul>
      )}

      {adding && (
        <AddressForm
          label={label}
          address={address}
          setLabel={setLabel}
          setAddress={setAddress}
          saving={saveMutation.isPending}
          canSave={canSave}
          onSave={() => saveMutation.mutate()}
          onCancel={closeForm}
        />
      )}
    </div>
  )
}

function AddressForm({
  label,
  address,
  setLabel,
  setAddress,
  saving,
  canSave,
  onSave,
  onCancel,
}: {
  label: string
  address: string
  setLabel: (v: string) => void
  setAddress: (v: string) => void
  saving: boolean
  canSave: boolean
  onSave: () => void
  onCancel: () => void
}) {
  return (
    <div className="space-y-2 rounded-lg border border-border/80 bg-muted/30 p-2.5">
      <Input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        placeholder="Label (Home, Office…) — optional"
        maxLength={60}
        className="h-8 text-xs"
      />
      <Textarea
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder="House / flat, street, area, landmark"
        rows={2}
        className="resize-none text-xs"
      />
      <div className="flex items-center justify-end gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          onClick={onCancel}
          className="h-7 gap-1 text-xs"
        >
          <X className="h-3 w-3" /> Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={!canSave}
          onClick={onSave}
          className="h-7 gap-1 text-xs"
        >
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
          Save
        </Button>
      </div>
    </div>
  )
}
